import { useContext, useEffect, useState } from "react";
import { AppGlobalContext } from "../contexts/AppContext.context";
import cryptoJs from "../utils/cryptoJs/crypto";
import { checkAuthStatus } from "../utils/firebase/firebase.config";
import { useSnackbar } from "../contexts/Snackbar.context";
import userManagementService from "../services/UserManagementService";
import { SNACKBAR_VARIANT, APP_MESSAGE } from "../config/Messages.config";
import { useNavigate } from "react-router-dom";

const authKey = process.env.REACT_APP_AUTHENTICATION_KEY;

const useAuthenticationHook = () => {
  const authSession = getAuthSession();
  const [role, setRole] = useState(authSession?.role || null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(authSession || null);
  const [firebaseProfile, setFirebaseProfile] = useState(null);
  const { SetAppState } = useContext(AppGlobalContext);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { AUTHENTICATION } = APP_MESSAGE;
  const { ERROR } = SNACKBAR_VARIANT;

  useEffect(() => {
    checkAuthStatus(setAuthStatus); // Firebase listener for auth changes
  }, []);

  // Listen for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout-event') {
        // Logout event detected from another tab
        handleCrossTabLogout();
      }
    };

    const handleCrossTabLogout = () => {
      // Clear all auth state
      setRole(null);
      setCurrentUser(null);
      setFirebaseProfile(null);
      setAuthStatus(null);
      
      // Update app state
      SetAppState({
        isAuthUser: false,
        loggedInUserRole: null,
        authUserDetails: null,
      });
      
      // Clear session storage
      window.sessionStorage.clear();
      
      // Only navigate and show notification if not already on login page
      navigate("/");
      showSnackbar(APP_MESSAGE.CROSS_TAB_LOGOUT.MESSAGE, SNACKBAR_VARIANT.INFO);
    };

    // Add event listener
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [SetAppState, navigate]);

  // Fetch user from backend by UID
  const fetchUserByUid = async (firebaseUser) => {
    if (!firebaseUser || !firebaseUser.uid) {
      console.error("Invalid Firebase user object.");
      showSnackbar(AUTHENTICATION.INVALID_USER, ERROR);
      return null;
    }

    try {
      setIsLoadingAuth(true)
      const response = await userManagementService.getById(firebaseUser.uid);
      const { data, statusCode } = response;

      // Check HTTP status codes
      if (statusCode !== 200) {
        console.warn(`Failed to fetch user. ${statusCode}`, data);
        showSnackbar(AUTHENTICATION.FAILED_TO_FETCH, ERROR);
        return null;
      }
      return data;
    } catch (error) {
      console.error("Error while fetching user by UID:", error);
      // Display user-friendly error message
      // showSnackbar(AUTHENTICATION.ERROR, ERROR);
      return null;
    } finally {
      setIsLoadingAuth(false)
    }
    // const data = {
    //   role: 'admin',
    //   uid: firebaseUser.uid,
    //   name: firebaseUser?.displayName || "Shreyas Bhumkar",
    //   email: firebaseUser?.email || "shreyas@gmail.com",
    // }
    // return data;
  };

  useEffect(() => {
    if (authStatus) {
      (async () => {
        try {
          // Fetch user from backend
          const userData = await fetchUserByUid(authStatus);
          if (!userData?.uid) {
            // New user → trigger onboarding
            navigate("/onboarding");
            setFirebaseProfile({
              uid: authStatus?.uid,
              email: authStatus?.email || null,
              displayName: authStatus?.displayName || null,
              photoURL: authStatus?.photoURL || null,
            });
            setRole(null);
            setCurrentUser(null);
            SetAppState({
              isAuthUser: false,
              loggedInUserRole: null,
              authUserDetails: null,
            });
            addAuthSession(null);
            return;
          }
          setCurrentUser(userData);
          setRole(userData?.role);
          SetAppState({
            isAuthUser: true,
            loggedInUserRole: userData?.role,
            authUserDetails: userData,
          });
          addAuthSession(userData);
        } catch (error) {
          showSnackbar("Login Error. Please try again.", "error");
          setRole(null);
          setCurrentUser(null);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  useEffect(() => {
    if (!authSession) setRole(null);
  }, [authSession]);

  const completeOnboarding = (userData) => {
    if (!userData) return;
    setCurrentUser(userData);
    setRole(userData?.role || null);
    SetAppState({
      isAuthUser: true,
      loggedInUserRole: userData?.role || null,
      authUserDetails: userData,
    });
    addAuthSession(userData);
    setFirebaseProfile(userData);
    setAuthStatus(userData);
    navigate("/");
    window.location.reload();
  };

  return { role, currentUser, isLoadingAuth, firebaseProfile, completeOnboarding };
};

export default useAuthenticationHook;

// Session helpers
const addAuthSession = (data) => {
  const encrypted = cryptoJs.encryptData(JSON.stringify(data));
  sessionStorage.setItem(authKey, encrypted);
};

const getAuthSession = () => {
  const encrypted = sessionStorage.getItem(authKey);
  return encrypted ? JSON.parse(cryptoJs.decryptData(encrypted)) : null;
};
